import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { AppShell } from "@/components/layout/AppShell";
import { AddBookCard, ProjectCard } from "@/components/projects/ProjectCard";
import { Button, Spinner } from "@/components/ui";
import { db, functions } from "@/config/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useProjects } from "@/hooks/useProjects";
import { PressEyebrow, PressPanel, ReadinessRing, StatusPill } from "@/components/pullpress/PressVisuals";
import type { AuthorAccount, Project, StateMachineState } from "@/types";

type LibraryFilter = "all" | "draft" | "progress" | "editing" | "ready" | "published";

const FILTERS: readonly { label: string; value: LibraryFilter; countKey: keyof ReturnType<typeof buildSummary> }[] = [
  { label: "All books", value: "all", countKey: "total" },
  { label: "Raw", value: "draft", countKey: "draft" },
  { label: "In production", value: "progress", countKey: "progress" },
  { label: "Editing", value: "editing", countKey: "editing" },
  { label: "Ready", value: "ready", countKey: "ready" },
  { label: "Published", value: "published", countKey: "published" },
];

const FLOW_STEPS: readonly { label: string; states: readonly StateMachineState[] }[] = [
  { label: "Source", states: ["CREATED", "INGESTED", "TRANSCRIBING"] },
  { label: "Voice", states: ["TRANSCRIBED", "ANALYZING"] },
  { label: "Map", states: ["ANALYZED", "STRUCTURING", "STRUCTURED"] },
  { label: "Manuscript", states: ["DRAFTING", "DRAFTED"] },
  { label: "Proof", states: ["AWAITING_REVIEW", "REVISED", "AUTO_ADVANCED"] },
  { label: "Edit", states: ["EDITING", "EDITED"] },
  { label: "Packet", states: ["PACKAGING", "PACKAGED"] },
  { label: "Release", states: ["PUBLISHING", "PUBLISHED"] },
];

export function DashboardPage() {
  const { user } = useAuthContext();
  const { projects, loading } = useProjects(user?.uid ?? null);
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const activeProject = projects[0];
  const summary = useMemo(() => buildSummary(projects), [projects]);
  const filteredProjects = useMemo(() => projects.filter((project) => filter === "all" || bucketFor(project) === filter), [filter, projects]);

  return (
    <AppShell variant="auth">
      <div className="mx-auto grid max-w-[1320px] gap-5 px-4 py-6 sm:px-6 xl:grid-cols-[minmax(0,1fr)_320px] lg:px-8">
        <section>
          <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-display text-[clamp(2.15rem,3.3vw,3.45rem)] font-semibold leading-tight tracking-as-snug text-ink-1">
                Books in motion
              </h1>
              <p className="mt-1 font-ui text-base text-ink-2">Every source, proof, edit, and release packet moving through one production pipe.</p>
            </div>
            <Link to="/new-project">
              <Button variant="walnut" size="lg">Start a book run</Button>
            </Link>
          </header>

          <ProductionSpine project={activeProject} />

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1 rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/75 p-1 shadow-as-1 backdrop-blur-2xl">
              {FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setFilter(item.value)}
                  className={`min-h-10 rounded-as-2 px-3 py-2 font-ui text-sm font-semibold transition-colors ${filter === item.value ? "bg-walnut text-paper-raised shadow-as-1" : "text-ink-3 hover:bg-paper hover:text-ink-1"}`}
                >
                  {item.label} <span className="ml-1 font-mono text-[10px]">{summary[item.countKey]}</span>
                </button>
              ))}
            </div>
            <div className="rounded-as-2 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/80 px-3 py-2 shadow-as-1">
              <PressEyebrow>{filteredProjects.length} in view</PressEyebrow>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : projects.length === 0 ? (
            <EmptyLibrary />
          ) : filteredProjects.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-3 xl:grid-cols-2">
              {filteredProjects.map((project) => <ProjectCard key={project.id} project={project} />)}
              <AddBookCard />
            </div>
          ) : (
            <EmptyFilter filter={filter} />
          )}
        </section>

        <aside className="space-y-3 xl:sticky xl:top-24 xl:self-start">
          <TaskPanel project={activeProject} />
          <ActivityPanel projects={projects} />
          {user && <PayoutSetupPanel userId={user.uid} />}
          <ProductionStatus summary={summary} />
          <PressPanel className="p-4">
            <p className="font-display text-lg italic leading-tight text-ink-2">
              The book moves when the next clear action is visible.
            </p>
          </PressPanel>
        </aside>
      </div>
    </AppShell>
  );
}

function ProductionSpine({ project }: { project?: Project }) {
  const activeIndex = project ? flowIndex(project.state) : -1;
  return (
    <PressPanel className="mt-5 p-4">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <PressEyebrow>Production spine</PressEyebrow>
          <p className="mt-1 font-ui text-sm text-ink-2">
            {project ? `Next for "${project.title}": ${nextAction(project.state)}.` : "Start a book run to open the production path."}
          </p>
        </div>
        <StatusPill tone={project ? statusTone(project.state) : "draft"}>{project ? stateLabel(project.state) : "No book running"}</StatusPill>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
        {FLOW_STEPS.map((step, index) => {
          const complete = activeIndex >= 0 && index < activeIndex;
          const active = index === activeIndex;
          return (
            <div key={step.label} className={`rounded-as-2 border px-3 py-2 ${active ? "border-walnut bg-walnut-tint" : complete ? "border-verify-soft/40 bg-verify-tint" : "border-rule bg-paper-raised/70"}`}>
              <p className={`font-ui text-sm font-semibold ${active ? "text-walnut-deep" : complete ? "text-verify" : "text-ink-3"}`}>{step.label}</p>
              <div className={`mt-2 h-1.5 rounded-full ${active ? "bg-walnut" : complete ? "bg-verify" : "bg-rule"}`} />
            </div>
          );
        })}
      </div>
    </PressPanel>
  );
}

function EmptyFilter({ filter }: { filter: LibraryFilter }) {
  return (
    <PressPanel className="mt-6 p-6">
      <PressEyebrow>No matches</PressEyebrow>
      <h2 className="mt-3 font-display text-2xl font-semibold text-ink-1">No books in {filterLabel(filter)}.</h2>
      <p className="mt-2 font-ui text-sm leading-as-read text-ink-3">Change the filter or start the next book run.</p>
    </PressPanel>
  );
}

function EmptyLibrary() {
  return (
    <PressPanel className="mt-6 p-8 text-center">
      <PressEyebrow className="justify-center">No books running yet</PressEyebrow>
      <h2 className="mt-3 font-display text-3xl font-semibold text-ink-1">The first book starts with one source.</h2>
      <p className="mx-auto mt-3 max-w-[500px] font-text text-base leading-as-read text-ink-2">
        Name the book, choose the release path, and bring in the raw material PullPress will shape.
      </p>
      <Link to="/new-project" className="mt-6 inline-block">
        <Button variant="walnut" size="lg">Start the first run</Button>
      </Link>
    </PressPanel>
  );
}

function TaskPanel({ project }: { project?: Project }) {
  const tasks = project
    ? [
        { title: `Move "${project.title}" forward`, meta: "Next author action", to: `/project/${project.id}/studio` },
        { title: "Check the production spine", meta: "Open book state", to: `/project/${project.id}` },
        { title: "Work the proof", meta: "Author and editor approval", to: `/project/${project.id}/review` },
        { title: "Set payout account", meta: "Required for royalties", to: "/dashboard?panel=payouts" },
      ]
    : [
        { title: "Start the first book", meta: "Create the production run", to: "/new-project" },
        { title: "Choose the help level", meta: "Pick self-serve or editor support", to: "/pricing" },
        { title: "Ask for guidance", meta: "Open support", to: "/support" },
      ];
  return (
    <PressPanel className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink-1">Next moves</h2>
        <PressEyebrow>{tasks.length} open</PressEyebrow>
      </div>
      <div className="mt-4 space-y-1">
        {tasks.map((task, index) => (
          <Link key={task.title} to={task.to} className="grid grid-cols-[18px_1fr] gap-3 border-b border-rule-faint py-2.5 last:border-0 hover:text-walnut-deep">
            <span className={`mt-1 h-3 w-3 rounded-full border ${index === 0 ? "border-verify bg-verify" : "border-walnut-soft bg-paper-raised"}`} />
            <div>
              <p className="font-display text-[15px] font-semibold text-ink-1">{task.title}</p>
              <p className="font-text text-sm text-ink-3">{task.meta}</p>
            </div>
          </Link>
        ))}
      </div>
    </PressPanel>
  );
}

function ActivityPanel({ projects }: { projects: Project[] }) {
  const activity = projects.slice(0, 5).map((project) => ({
    title: `${project.title} is ${stateLabel(project.state)}`,
    date: formatProjectDate(project),
  }));
  return (
    <PressPanel className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink-1">Recent movement</h2>
        <PressEyebrow>Production log</PressEyebrow>
      </div>
      <div className="mt-4 space-y-1">
        {activity.length > 0 ? activity.map((item) => (
          <div key={`${item.title}-${item.date}`} className="border-b border-rule-faint py-2.5 last:border-0">
            <p className="font-display text-base text-ink-1">{item.title}</p>
            <p className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-4">{item.date}</p>
          </div>
        )) : (
          <p className="font-ui text-sm leading-as-read text-ink-3">Movement appears after the first book run is created or updated.</p>
        )}
      </div>
    </PressPanel>
  );
}

function ProductionStatus({ summary }: { summary: ReturnType<typeof buildSummary> }) {
  return (
    <PressPanel className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink-1">Production state</h2>
          <p className="mt-1 font-text text-sm text-ink-3">Where the books stand now.</p>
        </div>
        <ReadinessRing value={summary.total === 0 ? 0 : Math.round((summary.ready + summary.published) / summary.total * 100)} label="Ready" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <StatusMetric label="In press" value={summary.progress} tone="progress" />
        <StatusMetric label="Editing" value={summary.editing} tone="editing" />
        <StatusMetric label="Ready" value={summary.ready} tone="ready" />
        <StatusMetric label="Published" value={summary.published} tone="published" />
      </div>
    </PressPanel>
  );
}

function StatusMetric({ label, value, tone }: {
  label: string;
  value: number;
  tone: "progress" | "editing" | "ready" | "published";
}) {
  return (
    <div>
      <StatusPill tone={tone}>{label}</StatusPill>
      <p className="mt-2 font-display text-2xl font-semibold text-ink-1">{value}</p>
    </div>
  );
}

function PayoutSetupPanel({ userId }: { userId: string }) {
  const [account, setAccount] = useState<AuthorAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(doc(db, "author_accounts", userId), (snap) => {
      setAccount(snap.exists() ? snap.data() as AuthorAccount : null);
      setLoading(false);
    }, (err) => {
      setError(err.message);
      setLoading(false);
    });
  }, [userId]);

  const startConnectOnboarding = async () => {
    setConnecting(true);
    setError(null);
    try {
      const createConnectAccount = httpsCallable<
        { refresh_url: string; return_url: string },
        { onboarding_url: string }
      >(functions, "createConnectAccount");
      const baseUrl = window.location.origin;
      const result = await createConnectAccount({
        refresh_url: `${baseUrl}/dashboard?connect=refresh`,
        return_url: `${baseUrl}/dashboard?connect=complete`,
      });
      window.location.assign(result.data.onboarding_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stripe Connect onboarding failed.");
      setConnecting(false);
    }
  };

  const payoutReady = account?.payout_method === "stripe_connect" && Boolean(account.stripe_connect_id);

  return (
    <PressPanel className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink-1">Royalties</h2>
          <p className="mt-1 font-text text-sm text-ink-3">Earnings appear after sales data is imported.</p>
        </div>
        <StatusPill tone={payoutReady ? "ready" : "draft"}>{payoutReady ? "Connected" : "Setup needed"}</StatusPill>
      </div>
      {error && (
        <div className="mt-4 rounded-as-2 border border-flag bg-flag-tint p-3 font-mono text-[10px] uppercase tracking-as-eyebrow text-flag">
          {error}
        </div>
      )}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <RoyaltyStat label="Balance" value={`$${(account?.current_balance ?? 0).toFixed(2)}`} />
        <RoyaltyStat label="Earned" value={`$${(account?.total_earned ?? 0).toFixed(2)}`} />
        <RoyaltyStat label="Payouts" value={payoutReady ? "Ready" : "Pending"} />
      </div>
      <Button onClick={startConnectOnboarding} variant="walnut" disabled={loading || connecting} className="mt-5 w-full">
        {connecting ? "Opening payout setup..." : payoutReady ? "Update payout account" : "Set up royalty payouts"}
      </Button>
    </PressPanel>
  );
}

function bucketFor(project: Project): Exclude<LibraryFilter, "all"> {
  if (project.state === "CREATED" || project.state === "INGESTED") return "draft";
  if (project.state === "EDITING" || project.state === "AWAITING_REVIEW" || project.state === "REVISED") return "editing";
  if (project.state === "PACKAGED" || project.state === "EDITED") return "ready";
  if (project.state === "PUBLISHED") return "published";
  return "progress";
}

function filterLabel(filter: LibraryFilter): string {
  if (filter === "all") return "all books";
  return filter.replace("_", " ");
}

function stateLabel(state: StateMachineState): string {
  const labels: Record<StateMachineState, string> = {
    CREATED: "raw source",
    INGESTED: "source in",
    TRANSCRIBING: "voice capture",
    TRANSCRIBED: "voice mapped",
    ANALYZING: "finding shape",
    ANALYZED: "shape found",
    STRUCTURING: "chapter map",
    STRUCTURED: "map ready",
    DRAFTING: "building manuscript",
    DRAFTED: "proof ready",
    AWAITING_REVIEW: "proof open",
    REVISED: "revision in",
    AUTO_ADVANCED: "proof closed",
    EDITING: "editor at work",
    EDITED: "edit complete",
    PACKAGING: "packet build",
    PACKAGED: "packet ready",
    PUBLISHING: "releasing",
    PUBLISHED: "published",
    ERROR: "blocked",
  };
  return labels[state];
}

function flowIndex(state: StateMachineState): number {
  if (state === "ERROR") return -1;
  const index = FLOW_STEPS.findIndex((step) => step.states.includes(state));
  return index < 0 ? 0 : index;
}

function nextAction(state: StateMachineState): string {
  if (state === "CREATED" || state === "INGESTED") return "bring in source material";
  if (state === "DRAFTED" || state === "AWAITING_REVIEW") return "work the proof";
  if (state === "PACKAGED") return "check the release packet";
  if (state === "PUBLISHED") return "track sales and royalties";
  if (state === "ERROR") return "resolve the blocker";
  return "let production continue";
}

function statusTone(state: StateMachineState): "draft" | "progress" | "editing" | "ready" | "published" | "blocked" {
  if (state === "ERROR") return "blocked";
  if (state === "PUBLISHED") return "published";
  if (state === "PACKAGED" || state === "EDITED") return "ready";
  if (state === "AWAITING_REVIEW" || state === "REVISED" || state === "EDITING") return "editing";
  if (state === "CREATED" || state === "INGESTED") return "draft";
  return "progress";
}

function formatProjectDate(project: Project): string {
  const millis = project.updated_at?.toMillis?.() ?? project.created_at?.toMillis?.();
  if (!millis) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(millis));
}

function RoyaltyStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-r border-rule pr-3 last:border-0">
      <p className="font-display text-xl font-semibold text-ink-1">{value}</p>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-as-eyebrow text-ink-4">{label}</p>
    </div>
  );
}

function buildSummary(projects: Project[]) {
  return projects.reduce(
    (summary, project) => {
      summary.total += 1;
      if (project.state === "CREATED" || project.state === "INGESTED") summary.draft += 1;
      else if (project.state === "EDITING" || project.state === "AWAITING_REVIEW" || project.state === "REVISED") summary.editing += 1;
      else if (project.state === "PACKAGED" || project.state === "EDITED") summary.ready += 1;
      else if (project.state === "PUBLISHED") summary.published += 1;
      else summary.progress += 1;
      return summary;
    },
    { total: 0, draft: 0, progress: 0, editing: 0, ready: 0, published: 0 },
  );
}
